import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import ProfileSwitcher from './ProfileSwitcher';
import {
  preloadPricing, preloadBooking, preloadMiniGames,
  preloadStudentDash, preloadAdminDash,
  preloadResources, preloadAbout,
} from '../utils/routePreload';

/**
 * Tiny FR | EN toggle. Keeps one active state, no dropdown — fastest UX.
 * Same component renders inline for desktop and stacked for mobile (the
 * parent decides spacing).
 */
const LangToggle = ({ className = '', testIdSuffix = '' }) => {
  const { lang, setLang } = useLang();
  const baseBtn = 'px-1.5 py-0.5 text-[11px] uppercase tracking-[0.18em] transition-colors';
  return (
    <div className={`inline-flex items-center gap-0.5 text-[#5D4E37]/70 select-none ${className}`} data-testid={`lang-toggle${testIdSuffix}`}>
      <button
        type="button"
        onClick={() => setLang('fr')}
        aria-pressed={lang === 'fr'}
        data-testid={`lang-fr${testIdSuffix}`}
        className={`${baseBtn} ${lang === 'fr' ? 'text-[#5D4E37] font-medium' : 'hover:text-[#5D4E37]'}`}
      >
        FR
      </button>
      <span aria-hidden="true" className="text-[#5D4E37]/30">|</span>
      <button
        type="button"
        onClick={() => setLang('en')}
        aria-pressed={lang === 'en'}
        data-testid={`lang-en${testIdSuffix}`}
        className={`${baseBtn} ${lang === 'en' ? 'text-[#5D4E37] font-medium' : 'hover:text-[#5D4E37]'}`}
      >
        EN
      </button>
    </div>
  );
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { user, logout, loading } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();

  // Guard against rapid double-clicks on "Mon espace" / "Connexion" — when the
  // browser is mid-transition, the next click would otherwise be enqueued and
  // could fire after auth state changes, producing a confusing redirect.
  const navLockRef = React.useRef(false);
  const safeNavigate = React.useCallback((to) => {
    if (navLockRef.current) return;
    navLockRef.current = true;
    setIsMenuOpen(false);
    navigate(to);
    // Release the lock after the route has committed.
    window.setTimeout(() => { navLockRef.current = false; }, 250);
  }, [navigate]);

  const handleLogout = async () => {
    if (navLockRef.current) return;
    navLockRef.current = true;
    try {
      await logout();
    } finally {
      setIsMenuOpen(false);
      navigate('/');
      window.setTimeout(() => { navLockRef.current = false; }, 250);
    }
  };

  const handleLoginClick = () => safeNavigate('/login');

  // "Mon espace" target — resolved here so the button rendering stays simple
  // and the click handler is a single source of truth.
  const monEspaceTarget = user?.role === 'admin' ? '/admin' : '/dashboard';
  const handleMonEspaceClick = () => safeNavigate(monEspaceTarget);
  const handleReserveClick = () => safeNavigate('/booking');

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-[#EDE8DC]/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex justify-between items-center h-20">
          {/* Brand Name */}
          <Link to="/" className="flex flex-col group min-w-0">
            <div className="text-xl sm:text-2xl font-serif font-semibold text-[#5D4E37] tracking-tight leading-tight whitespace-nowrap">
              Savarit Lessons
            </div>
            <div className="uppercase !font-['Georgia'] !font-light !text-[10px] sm:!text-xs mt-0.5 text-[#D4A574] whitespace-nowrap">
              {t('Leçons privées à domicile')}
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8 xl:space-x-10">
            <Link to="/" className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide">
              {t('Accueil')}
            </Link>
            <Link to="/tarifs" onMouseEnter={preloadPricing} onFocus={preloadPricing} className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide">
              {t('Tarifs')}
            </Link>
            <Link to="/ressources" onMouseEnter={preloadResources} onFocus={preloadResources} className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide" data-testid="header-resources-link">
              {t('Ressources')}
            </Link>
            <Link to="/a-propos" onMouseEnter={preloadAbout} onFocus={preloadAbout} className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide" data-testid="header-about-link">
              {t('À propos')}
            </Link>
            <Link to="/mini-jeux" onMouseEnter={preloadMiniGames} onFocus={preloadMiniGames} className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide">
              {t('Entraînement')}
            </Link>
            {loading ? (
              // Stable placeholder during the brief auth-check window so the
              // header layout doesn't jump and a too-early click is never lost
              // on an unrendered button. Same width as the resolved CTAs.
              <div className="flex items-center gap-3 pl-2" aria-hidden="true">
                <div className="h-10 w-[140px] rounded-sm bg-[#5D4E37]/10" />
              </div>
            ) : user ? (
                  <div className="flex items-center gap-3 pl-2">
                    {user.role !== 'admin' && <ProfileSwitcher />}
                    <Button
                      type="button"
                      onClick={handleMonEspaceClick}
                      onMouseEnter={user.role === 'admin' ? preloadAdminDash : preloadStudentDash}
                      onFocus={user.role === 'admin' ? preloadAdminDash : preloadStudentDash}
                      data-testid="header-cta-mon-espace"
                      className="bg-[#5D4E37] hover:bg-[#6B5D4F] text-white text-sm font-light px-5 h-10 rounded-sm tracking-wide transition-shadow duration-200 hover:shadow-[0_8px_24px_-10px_rgba(93,78,55,0.4)]"
                    >
                      {t('Mon espace')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      className="text-[#5D4E37] hover:text-[#D4A574] hover:bg-transparent h-10 w-10 p-0"
                      aria-label="Déconnexion"
                    >
                      <LogOut className="w-4 h-4" />
                    </Button>
                    <LangToggle className="ml-1" />
                  </div>
                ) : (
                  <div className="flex items-center gap-4 pl-2">
                    <button
                      type="button"
                      onClick={handleLoginClick}
                      data-testid="header-login-link"
                      className="text-sm font-light text-[#5D4E37]/70 hover:text-[#5D4E37] tracking-wide transition-colors"
                    >
                      {t('Connexion')}
                    </button>
                    <Button
                      type="button"
                      onClick={handleReserveClick}
                      onMouseEnter={preloadBooking}
                      onFocus={preloadBooking}
                      data-testid="header-cta-reserver"
                      className="bg-[#5D4E37] hover:bg-[#6B5D4F] text-white text-sm font-light px-5 h-10 rounded-sm tracking-wide transition-shadow duration-200 hover:shadow-[0_8px_24px_-10px_rgba(93,78,55,0.4)]"
                    >
                      {t('Réserver un cours')}
                    </Button>
                    <LangToggle />
                  </div>
                )}
          </nav>

          {/* Mobile menu button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden p-2 hover:bg-[#EDE8DC]/30 rounded-sm transition-colors"
          >
            {isMenuOpen ? (
              <X className="w-6 h-6 text-[#5D4E37]" />
            ) : (
              <Menu className="w-6 h-6 text-[#5D4E37]" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden py-6 border-t border-[#EDE8DC]/40">
            <nav className="flex flex-col space-y-5">
              <Link 
                to="/" 
                className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('Accueil')}
              </Link>
              <Link 
                to="/tarifs" 
                className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('Tarifs')}
              </Link>
              <Link 
                to="/ressources" 
                className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('Ressources')}
              </Link>
              <Link 
                to="/a-propos" 
                className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('À propos')}
              </Link>
              <Link 
                to="/mini-jeux" 
                className="text-sm font-light text-[#5D4E37] hover:text-[#D4A574] transition-colors tracking-wide"
                onClick={() => setIsMenuOpen(false)}
              >
                {t('Entraînement')}
              </Link>
              {loading ? (
                <div className="pt-2" aria-hidden="true">
                  <div className="h-11 w-full rounded-sm bg-[#5D4E37]/10" />
                </div>
              ) : user ? (
                    <>
                      {user.role !== 'admin' && (
                        <div className="pb-2">
                          <ProfileSwitcher />
                        </div>
                      )}
                      <Button
                        type="button"
                        onClick={handleMonEspaceClick}
                        data-testid="header-mobile-cta-mon-espace"
                        className="mt-2 bg-[#5D4E37] hover:bg-[#6B5D4F] text-white text-sm font-light w-full h-11 rounded-sm tracking-wide"
                      >
                        {t('Mon espace')}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleLogout}
                        className="text-[#5D4E37] hover:text-[#D4A574] justify-start hover:bg-transparent px-0"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        {t('Déconnexion')}
                      </Button>
                    </>
                  ) : (
                    <div className="flex flex-col gap-3 pt-2">
                      <Button
                        type="button"
                        onClick={handleReserveClick}
                        data-testid="header-mobile-cta-reserver"
                        className="bg-[#5D4E37] hover:bg-[#6B5D4F] text-white text-sm font-light w-full h-11 rounded-sm tracking-wide"
                      >
                        {t('Réserver un cours')}
                      </Button>
                      <button
                        type="button"
                        onClick={handleLoginClick}
                        data-testid="header-mobile-login-link"
                        className="text-sm font-light text-[#5D4E37]/70 hover:text-[#5D4E37] tracking-wide transition-colors text-center"
                      >
                        {t('Connexion')}
                      </button>
                    </div>
                  )}
              <div className="pt-2 border-t border-[#EDE8DC]/50 flex justify-center">
                <LangToggle testIdSuffix="-mobile" />
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default React.memo(Header);
